// Static catalogue of REAL processed-episode deliverables shown in the
// "Real Output Examples" trust section. Adding a new example = appending
// one object here. No CMS, no backend, no admin tooling.
//
// IMPORTANT: every field is consumed somewhere in the UI / SEO layer.
// Read EpisodeExampleDetail.js + EpisodeExamplesHub.js + Landing.js
// before mutating the shape.

export const EXAMPLES = [
  {
    slug: "mfm-10k-to-1m",
    title: "Go from $10K to $1M in just 3 years",
    subtitle: "My First Million",
    sourcePodcast: "My First Million",
    coverImage: "/examples/mfm-10k-to-1m/previews/cover.jpg",
    coverImageLarge: "/examples/mfm-10k-to-1m/previews/cover-large.jpg",
    pdfUrl: "/examples/mfm-10k-to-1m/episode.pdf",
    intro:
      "Inspect the complete deliverable EpisodeKit generated from a single uploaded episode of My First Million — 99% accurate transcript, editorial show notes, an SEO blog post, 12+ platform-tuned distribution assets, and YouTube outputs. Every asset is publish-ready. No editing, no manual cleanup.",
    meta: {
      duration: "78 min",
      language: "English",
      tone: "Professional · Editorial · Conversational",
      input: "Audio · Podcast Episode",
    },
    includedOutputs: [
      "Full Transcript",
      "Show Notes",
      "Chapters",
      "SEO Blog Post",
      "Twitter / X Post",
      "LinkedIn Post",
      "Newsletter Blurb",
      "Best Quotes",
      "Discovery Titles",
      "TikTok Caption",
      "Instagram Captions",
      "YouTube Description",
      "Pinned Comment",
      "Shorts Hooks",
      "SRT Subtitles",
      "ZIP Export",
    ],
    landingHighlights: [
      "Full Transcript",
      "SEO Blog Post",
      "Show Notes",
      "Shorts Hooks",
      "YouTube Description",
      "Social Captions",
    ],
    previews: [
      {
        src: "/examples/mfm-10k-to-1m/previews/transcript.jpg",
        alt: "Full transcript page from the My First Million episode deliverable, generated by EpisodeKit.",
        caption: "Full transcript — 99% accurate, formatted, timestamped.",
      },
      {
        src: "/examples/mfm-10k-to-1m/previews/show-notes.jpg",
        alt: "Editorial show notes page with summary, takeaways, and quotes generated for the My First Million episode.",
        caption: "Editorial show notes — publish straight to your podcast page.",
      },
      {
        src: "/examples/mfm-10k-to-1m/previews/blog-post.jpg",
        alt: "SEO-optimised long-form blog post page generated from the podcast episode.",
        caption: "SEO blog post — long-form, ranking-ready, drop into any CMS.",
      },
      {
        src: "/examples/mfm-10k-to-1m/previews/quotes.jpg",
        alt: "Best quotes pull-out page from the deliverable, ready for graphics and threads.",
        caption: "Best quotes — ready for threads, reels, and graphics.",
      },
      {
        src: "/examples/mfm-10k-to-1m/previews/discovery-titles.jpg",
        alt: "Discovery Titles page — SEO-engineered Google/Blog and YouTube title variants generated for the My First Million episode.",
        caption: "Discovery Titles — SEO titles for Google + YouTube, highest-CTR variants tested per surface.",
      },
      {
        src: "/examples/mfm-10k-to-1m/previews/youtube.jpg",
        alt: "YouTube description page with hook, summary, takeaways, chapters, and hashtags.",
        caption: "YouTube description — SEO-rich, chaptered, CTA-ready.",
      },
    ],
    why: [
      {
        title: "Rank on Google",
        body: "Long-form transcript + SEO blog post + meta description turn every episode into a search-indexable asset.",
      },
      {
        title: "Get discovered on YouTube",
        body: "Hook-first description, chapter timestamps, pinned comment, and Shorts hooks built for the algorithm.",
      },
      {
        title: "Publish across every surface",
        body: "Native, platform-tuned posts for Twitter, LinkedIn, Instagram, TikTok, and email — no manual repurposing.",
      },
      {
        title: "Ship in minutes, not days",
        body: "One upload becomes 12+ publish-ready assets while you focus on the next episode.",
      },
    ],
    seo: {
      title:
        "Podcast Repurposing Example: 12+ Assets From One Episode | EpisodeKit",
      description:
        "See how EpisodeKit turned one 78-minute podcast episode into a transcript, show notes, SEO blog post, YouTube assets, quotes, social posts, and more.",
      ogImage: "/examples/mfm-10k-to-1m/previews/cover-large.jpg",
    },
  },
];

export const getExampleBySlug = (slug) =>
  EXAMPLES.find((e) => e.slug === slug) || null;
